<script setup lang="ts">
import { ref, useId } from "vue";
import { useUxData } from "@/stores/ux-data.ts";
import { useAuthDataStore } from "@/stores/auth-data.ts";
import { useContextDataStore } from "@/stores/context-data.ts";
import { useModal } from "@/composables/useModal";
import type { SubscriptionPlanInterface } from "@/types/auth.ts";
import SubscriptionManager from "@/components/account/SubscriptionManager.vue";

const ux = useUxData();
const auth = useAuthDataStore();
const context = useContextDataStore();

// SubscriptionManager mounts only while the modal is open; it creates its Stripe session on click.
const open = ref(false);
// The upgrade on sale, fetched on the first show and kept; a failed fetch is asked again on the next.
const upgradePlan = ref<SubscriptionPlanInterface | null>(null);
const loadingPlan = ref(false);
const modalRef = ref<HTMLElement | null>(null);
// More than one view may include the modal, so its heading's id is generated, never fixed.
const titleId = useId();

const loadUpgradePlan = () => {
  if (upgradePlan.value !== null || loadingPlan.value) return;
  loadingPlan.value = true;
  context.retrieveUpgradePlan()
      .then((plan) => { upgradePlan.value = plan; })
      .finally(() => { loadingPlan.value = false; });
};

const { show } = useModal(modalRef, {
  onShow: () => {
    open.value = true;
    loadUpgradePlan();
  },
  onHidden: () => { open.value = false; },
});

defineExpose({ show });
</script>

<template>
  <div ref="modalRef" class="modal fade premium-feature-modal" tabindex="-1" :aria-labelledby="titleId">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header h5 m-0">
          <span :id="titleId" class="modal-title">{{ ux?.labels?.profile?.subscription?.ctaHeading ?? 'Premium Feature' }}</span>
          <button
              type="button"
              class="btn btn-link"
              data-bs-dismiss="modal"
              :aria-label="ux.labels?.actions?.close ?? 'Close'"
          >
            <span class="fa-light fa-fw fa-times"></span>
          </button>
        </div>
        <div class="modal-body">
          <p>
            {{ ux.labels?.premiumFeature?.message ?? "As a Dicey Premium subscriber, you'll have access to all the features, and everything new as it becomes available. Create campaigns with setting rules and limit what elements characters can have and unlock full access to the library. If you don't see your favorite setting yet, let us know so we can discuss adding it with the publisher." }}
          </p>
          <!-- Held back until the plan arrives, so the free plan doesn't flash in its place. -->
          <p v-if="open && loadingPlan" class="text-center">
            <span class="fa-light fa-spinner-scale fa-spin"></span>
          </p>
          <SubscriptionManager
              v-else-if="open"
              :user="auth.user"
              :current-plan="upgradePlan"
              heading=""
          />
        </div>
      </div>
    </div>
  </div>
</template>
